import axios from "axios";
import {
  COST_GL_ENTITIES,
  COST_GL_PAGE_SIZE,
  MASTER_STATUS,
} from "@/constants/costGl";

// Accountant (cost_gl.manage) pages: assign a Cost G/L to the master
// treatments / labs / medicine & products so they become usable.
const base = clinicUrl => `/api/cost-gl/${clinicUrl}`;

const emptyCounts = () => ({
  [MASTER_STATUS.PENDING]: 0,
  [MASTER_STATUS.READY]: 0,
  [MASTER_STATUS.CANCELLED]: 0,
});

const moduleCostGl = {
  namespaced: true,
  state: {
    accounts: [],
    // per-entity tab counts, e.g. counts.treat.PENDING
    counts: {
      treat: emptyCounts(),
      lab: emptyCounts(),
      item: emptyCounts(),
    },
  },
  getters: {
    getAccounts: state => state.accounts,
    getCounts: state => entity => state.counts[entity] || emptyCounts(),
    getPendingCount: state => entity =>
      (state.counts[entity] || emptyCounts())[MASTER_STATUS.PENDING],
    // drives the red dot on the sidebar "การบัญชี" group
    hasAnyPending: state =>
      Object.values(state.counts).some(c => c[MASTER_STATUS.PENDING] > 0),
  },
  mutations: {
    setAccounts(state, accounts) {
      state.accounts = accounts || [];
    },
    setCounts(state, { entity, counts }) {
      state.counts = {
        ...state.counts,
        [entity]: { ...emptyCounts(), ...counts },
      };
    },
  },
  actions: {
    async fetchAccounts({ commit }, { clinicUrl }) {
      const res = await axios.get(`${base(clinicUrl)}/accounts`);
      const accounts = res.data.data || [];
      commit("setAccounts", accounts);
      return accounts;
    },

    // One tab of one master table. Always refreshes the tab badges.
    async fetchList(
      { commit },
      { clinicUrl, entity, status = MASTER_STATUS.PENDING, search, offset, limit = COST_GL_PAGE_SIZE }
    ) {
      const params = { status, limit };
      if (search) params.search = search;
      if (offset) params.offset = offset;
      const res = await axios.get(
        `${base(clinicUrl)}/${COST_GL_ENTITIES[entity].api}`,
        { params }
      );
      commit("setCounts", { entity, counts: res.data.counts });
      return res.data;
    },

    // Cheap refresh of just the badges (sidebar dot) for all three tables.
    async fetchPendingCounts({ commit }, { clinicUrl }) {
      await Promise.all(
        Object.keys(COST_GL_ENTITIES).map(async entity => {
          const res = await axios.get(
            `${base(clinicUrl)}/${COST_GL_ENTITIES[entity].api}`,
            { params: { status: MASTER_STATUS.PENDING, limit: 1 } }
          );
          commit("setCounts", { entity, counts: res.data.counts });
        })
      );
    },

    // PENDING -> READY, or change the Cost G/L of a READY row.
    async assign(_, { clinicUrl, entity, id, costGlId }) {
      const res = await axios.put(
        `${base(clinicUrl)}/${COST_GL_ENTITIES[entity].api}/${id}`,
        { costGlId }
      );
      return res.data;
    },

    // "ลบ" on a READY row: READY -> CANCELLED.
    async cancel(_, { clinicUrl, entity, id }) {
      const res = await axios.delete(
        `${base(clinicUrl)}/${COST_GL_ENTITIES[entity].api}/${id}`
      );
      return res.data;
    },
  },
};

export default moduleCostGl;
